/**
 * Chat Window Design Test Page
 * Prototype for V0-inspired chat interface improvements
 *
 * Access: http://flownotes.localhost/?test=chat-window
 *
 * Status:
 * - ✅ Phase 1: Bottom Toolbar (implemented)
 * - ✅ Phase 2: Mention Dropdown (implemented)
 * - ❌ Phase 3: File Review Section (NOT implemented - depends on diff infrastructure)
 * - ✅ Phase 4: Message Design (implemented)
 * - ✅ Phase 5: Design System (implemented)
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { ContentEditableChatInput } from './ui/contenteditable-chat-input'
import { CollapsibleModifiedFilesPanel } from '@/components/features/collapsible-modified-files-panel'
import { MentionAutocomplete } from './ui/mention-autocomplete'
import { FileEditWidget } from './features/file-edit-widget'
import { StreamingOutputWindow } from './features/chat-messages/streaming-output-window'
import { MarkdownEditor } from './features/markdown-editor'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { Brain, ArrowLeft, Plus, FileText, Mic, Inbox, Search, FolderOpen, Users, Clock, Sparkles, Square } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'
import { cn } from '@/lib/utils'
import type { DocumentReference } from './layout/chat-pane.types'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'

// Mock data – varied markdown to test rendering (lists, bold, code, blockquote)
const MOCK_MESSAGES = [
  {
    role: 'user' as const,
    content: 'Can you help me review the recent UX improvements?',
    timestamp: new Date(Date.now() - 300000)
  },
  {
    role: 'assistant' as const,
    content: `I'd be happy to help review the UX improvements. Based on recent work, here are the key changes:

**Improvements:**

1. **Bottom Toolbar Design** – Actions are now consolidated at the bottom of the input.
2. **Mention Autocomplete** – Shows suggested items when typing \`@\`.
3. **Simplified Message Design** – User prompts in bubbles; assistant output as plain text with proper markdown.

Would you like me to dive deeper into any of these?`,
    timestamp: new Date(Date.now() - 240000)
  },
  {
    role: 'user' as const,
    content: 'What about the file review mechanism?',
    timestamp: new Date(Date.now() - 120000)
  },
  {
    role: 'assistant' as const,
    content: `The file review mechanism (Phase 3) isn't implemented yet. It depends on the diff infrastructure.

**Current status**

| Area | Status |
|------|--------|
| FileEditWidget | ✅ Exists (used in messages) |
| Inline FileReviewSection | ❌ Not created |
| Diff preview integration | ❌ Pending |

**What's needed**

- Create \`FileReviewSection\` component (expandable, shows file list)
- Integrate with existing diff tracking
- Replace \`DiffPreviewDialog\` usage

> **Note:** Would you like me to implement Phase 3 next, or refine the current design first?`,
    timestamp: new Date(Date.now() - 60000)
  },
  {
    role: 'user' as const,
    content: 'Show me an example code snippet for the chat input.',
    timestamp: new Date(Date.now() - 30000)
  },
  {
    role: 'assistant' as const,
    content: `Here's a minimal example of wiring the contenteditable input with the mention dropdown:

\`\`\`tsx
<ContentEditableChatInput
  value={input}
  onChange={setInput}
  onSend={handleSend}
  placeholder="Ask or search..."
  references={references}
  onReferenceRemove={(id) => setReferences(r => r.filter(x => x.id !== id))}
  agentBadge={null}
/>
\`\`\`

The \`references\` prop controls the chips above the input; \`onReferenceRemove\` is called when the user removes a chip.`,
    timestamp: new Date(Date.now() - 15000)
  }
]

// Initial conversation for Full Chat tab so there's scrollable content without typing
const FULL_CHAT_INITIAL_HISTORY: { role: 'user' | 'assistant'; content: string; timestamp: Date }[] = MOCK_MESSAGES.map(m => ({
  role: m.role,
  content: m.content,
  timestamp: m.timestamp instanceof Date ? m.timestamp : new Date(m.timestamp)
}))


// Mock modified files for "Modified Files Above Chat" prototype (Summary View style)
const MOCK_MODIFIED_FILES = [
  { filePath: 'frontend/src/components/test-thinking-mode.tsx', status: 'new' as const, linesAdded: 770, linesRemoved: 0 },
  { filePath: 'frontend/src/App.tsx', status: 'modified' as const, linesAdded: 18, linesRemoved: 8 },
  { filePath: 'organize-todo-folders.js', status: 'modified' as const, linesAdded: 121, linesRemoved: 40 },
  { filePath: 'fix-todo-folder-names.js', status: 'new' as const, linesAdded: 244, linesRemoved: 0 },
  { filePath: '2025-11-27-multi-document-todo-pattern-and-triggers.md', status: 'new' as const, linesAdded: 857, linesRemoved: 0 },
  { filePath: 'thinking-mode-reasoning-ui-design.md', status: 'new' as const, linesAdded: 708, linesRemoved: 0 },
]

// Simulated output lines for Streaming output tab (enough to overflow and scroll)
const MOCK_COMMAND_LINES = [
  'mkdir -p /Users/rowan/hopwork/clients/msaliceb/.cursor/skills/doc-sync-commit',
  '```',
  '---',
  '## Streaming output',
  '',
  'Narrow scrollable output window for agent actions.',
  '---',
  'title: Chat streaming output window',
  'overview: Add a narrow, scrollable output window in chat.',
  '---',
  '',
  '# Goal',
  '',
  'When the agent is streaming or writing a document, show a **narrow scrollable output window**.',
  '',
  '# Phase 1',
  '',
  '## Step 1 — Reusable component',
  'Create a small panel for "agent output" (doc creation or command).',
  '',
  '## Step 2 — Integrate into test-chat-window',
  'Add a new tab that demos command block and doc creation block.',
  '',
  '## Step 3 — Prototype index',
  'Update Chat Window entry so streaming output panel is discoverable.',
  '',
  '---',
  '[DONE]',
]
const MOCK_DOC_LINES = [
  '---',
  'title: My New Document',
  'date: 2026-03-09',
  'status: draft',
  '---',
  '',
  '# Section 1',
  '',
  'Content here. The streaming output window should have a fixed height so that when new lines are added at the bottom, the content scrolls upward and the container stays the same size.',
  '',
  '## Subsection 1.1',
  '',
  'More body text to fill the view. Each line streams in and the scroll position follows the bottom, so the effect is that new content appears at the bottom and older content moves up.',
  '',
  '## Subsection 1.2',
  '',
  '- Bullet one',
  '- Bullet two',
  '- Bullet three',
  '',
  '# Section 2',
  '',
  'Second section. With enough lines the content will exceed the fixed-height container and a vertical scrollbar will appear. New lines are appended below; we keep scrollTop at scrollHeight so the latest line is always visible.',
  '',
  '```tsx',
  'useEffect(() => {',
  '  if (!scrollRef.current) return',
  '  scrollRef.current.scrollTop = scrollRef.current.scrollHeight',
  '}, [lines])',
  '```',
  '',
  '# Section 3',
  '',
  'Final section. This mock has many lines so the doc creation block clearly overflows its fixed height and the upward-scrolling behavior is obvious.',
  '',
  '---',
  'End of document.',
]

export function ChatWindowTest() {
  const [activeTab, setActiveTab] = useState<'toolbar' | 'messages' | 'mention' | 'review' | 'drag-drop' | 'copy-paste' | 'full' | 'chat-first' | 'modified-files-above-chat' | 'streaming-output'>('chat-first')
  const [input, setInput] = useState('')
  const [references, setReferences] = useState<DocumentReference[]>([])
  const [selectedModel, setSelectedModel] = useState('claude-sonnet-4-5-20250929')
  const [mentionQuery, setMentionQuery] = useState('')
  const [mentionPosition, setMentionPosition] = useState<{ top: number; left: number } | null>(null)
  const mentionSearchInputRef = useRef<HTMLInputElement>(null)
  const [editorViewMode, setEditorViewMode] = useState<'source' | 'wysiwyg'>('source')
  const [addMenuOpen, setAddMenuOpen] = useState(false)
  const [modifiedFilesPanelExpanded, setModifiedFilesPanelExpanded] = useState(false)

  // Full chat state - Conversation history
  interface ChatMessage {
    role: 'user' | 'assistant'
    content: string
    timestamp: Date
    wasStopped?: boolean
  }

  const [conversationHistory, setConversationHistory] = useState<ChatMessage[]>(FULL_CHAT_INITIAL_HISTORY)
  const [isResponding, setIsResponding] = useState(false)

  // Phase 1: Streaming state (for current message being generated)
  const [streamingText, setStreamingText] = useState('')  // Current streaming content
  const [fullResponse, setFullResponse] = useState('')  // Full response to stream
  const [streamingIndex, setStreamingIndex] = useState(0)  // Current char index

  // Phase 2: Stop button state
  const [wasStopped, setWasStopped] = useState(false)

  // Phase 3: Edit prompt state
  const [editingMessageIndex, setEditingMessageIndex] = useState<number | null>(null)
  const [editedPromptText, setEditedPromptText] = useState('')

  // Phase 4: Visual polish
  const [showLayoutGuides, setShowLayoutGuides] = useState(true)

  // Streaming output tab: demo state for command and doc creation blocks
  const [commandOutputLines, setCommandOutputLines] = useState<string[]>([])
  const [commandStreaming, setCommandStreaming] = useState(false)
  const [docOutputLines, setDocOutputLines] = useState<string[]>([])
  const [docStreaming, setDocStreaming] = useState(false)
  const commandIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const docIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Refs
  const responseEndRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Phase 1: Token-based streaming effect (more realistic than char-by-char)
  useEffect(() => {
    if (!isResponding || !fullResponse) return

    // Already finished streaming - save to history
    if (streamingIndex >= fullResponse.length) {
      setIsResponding(false)

      // Save completed assistant message to history
      setConversationHistory(prev => [...prev, {
        role: 'assistant',
        content: streamingText,
        timestamp: new Date(),
        wasStopped
      }])

      return
    }

    // Stream in chunks (tokens/words) - more realistic like real LLM APIs
    const timer = setTimeout(() => {
      // Calculate next chunk: either a full word or remaining text
      let chunkSize = 1
      const remaining = fullResponse.substring(streamingIndex)

      // Find next word boundary (space, newline, or punctuation)
      const nextBoundary = remaining.search(/[\s\n,.!?;:]/)
      if (nextBoundary > 0 && nextBoundary <= 8) {
        // Stream up to the next boundary (complete word/token)
        chunkSize = nextBoundary + 1
      } else if (nextBoundary === -1) {
        // No boundary found, stream rest
        chunkSize = remaining.length
      } else {
        // Long word, stream partial (3-5 chars at a time)
        chunkSize = Math.min(5, remaining.length)
      }

      setStreamingText(prev => prev + fullResponse.substring(streamingIndex, streamingIndex + chunkSize))
      setStreamingIndex(prev => prev + chunkSize)
    }, 30) // 30ms per token/chunk (faster, more realistic)

    return () => clearTimeout(timer)
  }, [isResponding, fullResponse, streamingIndex, streamingText, wasStopped])

  // Auto-scroll only when user is near bottom (stops scroll from jumping when reading above during streaming)
  const SCROLL_NEAR_BOTTOM_THRESHOLD = 100
  useEffect(() => {
    const el = scrollContainerRef.current
    if (!el || !(isResponding || conversationHistory.length > 0)) return
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    if (distanceFromBottom <= SCROLL_NEAR_BOTTOM_THRESHOLD) {
      el.scrollTop = el.scrollHeight
    }
  }, [streamingText, conversationHistory, isResponding])

  // Phase 2: Stop handler
  const handleStop = useCallback(() => {
    setIsResponding(false)
    setWasStopped(true)
    setFullResponse(streamingText) // Keep what we have so far

    // Save partial response to history
    setConversationHistory(prev => [...prev, {
      role: 'assistant',
      content: streamingText,
      timestamp: new Date(),
      wasStopped: true
    }])

    // Clear streaming state
    setStreamingText('')
    setStreamingIndex(0)
  }, [streamingText])

  // Phase 3: Edit/resubmit handler - rewind conversation
  const handleResubmit = useCallback(() => {
    if (!editedPromptText.trim() || editingMessageIndex === null) return

    // Truncate conversation history up to the edited message
    const newHistory = conversationHistory.slice(0, editingMessageIndex)

    // Add the edited user message
    newHistory.push({
      role: 'user',
      content: editedPromptText,
      timestamp: new Date()
    })

    setConversationHistory(newHistory)
    setEditingMessageIndex(null)
    setEditedPromptText('')

    // Start new streaming response
    setStreamingText('')
    setStreamingIndex(0)
    setWasStopped(false)
    setIsResponding(true)
    setFullResponse('')

    setTimeout(() => {
      const mockResponse = `Here's a new response based on your edited prompt: "${editedPromptText}"\n\nNotice:\n• Conversation was rewound to this point\n• Messages after your edit were removed\n• This is a fresh response\n\nThis is how message editing and rewinding works!`

      setFullResponse(mockResponse)
      setStreamingIndex(0)
    }, 800)
  }, [editedPromptText, editingMessageIndex, conversationHistory])

  // Mock draggable items for testing
  const mockDraggableItems = [
    { id: 'doc-1', title: 'Chat Window Design Review', type: 'work_spec', path: 'docs/implementations/2025-11-18-chat-window-design-review.md' },
    { id: 'doc-2', title: 'Weekly Scrum Notes', type: 'note', path: 'personal/2025-11-27.md' },
    { id: 'basecamp-todo-12345', title: 'Create map for self-selection responses', type: 'note', path: undefined },
    { id: 'basecamp-project-67890', title: 'GGIA Recommendation Engine', type: 'note', path: undefined },
  ]

  const tabs = [
    { id: 'chat-first', label: '✨ Chat-First Home' },
    { id: 'streaming-output', label: 'Streaming output' },
    { id: 'full', label: 'Full Chat' },
    { id: 'modified-files-above-chat', label: 'Modified Files Above Chat' },
    { id: 'toolbar', label: 'Toolbar Only' },
    { id: 'messages', label: 'Messages' },
    { id: 'mention', label: 'Mention Dropdown' },
    { id: 'review', label: 'File Review (Current)' },
    { id: 'drag-drop', label: 'Drag & Drop Test' },
    { id: 'copy-paste', label: 'Copy/Paste Test' },
  ]

  // Streaming output tab: run command demo (append lines on interval)
  const runCommandDemo = useCallback(() => {
    if (commandStreaming) return
    setCommandOutputLines([])
    setCommandStreaming(true)
    commandIntervalRef.current = setInterval(() => {
      setCommandOutputLines(prev => {
        if (prev.length >= MOCK_COMMAND_LINES.length) {
          if (commandIntervalRef.current) {
            clearInterval(commandIntervalRef.current)
            commandIntervalRef.current = null
          }
          setCommandStreaming(false)
          return prev
        }
        return [...prev, MOCK_COMMAND_LINES[prev.length]]
      })
    }, 150)
  }, [commandStreaming])

  const runDocDemo = useCallback(() => {
    if (docStreaming) return
    setDocOutputLines([])
    setDocStreaming(true)
    docIntervalRef.current = setInterval(() => {
      setDocOutputLines(prev => {
        if (prev.length >= MOCK_DOC_LINES.length) {
          if (docIntervalRef.current) {
            clearInterval(docIntervalRef.current)
            docIntervalRef.current = null
          }
          setDocStreaming(false)
          return prev
        }
        return [...prev, MOCK_DOC_LINES[prev.length]]
      })
    }, 120)
  }, [docStreaming])

  useEffect(() => {
    return () => {
      if (commandIntervalRef.current) clearInterval(commandIntervalRef.current)
      if (docIntervalRef.current) clearInterval(docIntervalRef.current)
    }
  }, [])

  const handleMentionClick = () => {
    setMentionQuery('')
    const rect = mentionSearchInputRef.current?.getBoundingClientRect()
    setMentionPosition(rect ? { top: rect.bottom + 6, left: rect.left } : { top: 300, left: 100 })
  }

  // Handlers for + menu items (simplified approach: trigger @ mention)
  const handleAddDocument = () => {
    setAddMenuOpen(false)
    setMentionQuery('')
    const rect = mentionSearchInputRef.current?.getBoundingClientRect()
    setMentionPosition(rect ? { top: rect.bottom + 6, left: rect.left } : { top: 400, left: 100 })
  }

  const handleAddVoiceMemo = () => {
    setAddMenuOpen(false)
    handleAddDocument()  // For now, same as add document
  }

  const handleAddFromInbox = () => {
    setAddMenuOpen(false)
    handleAddDocument()
  }

  const handleAddBasecampTodo = () => {
    setAddMenuOpen(false)
    handleAddDocument()
  }

  const handleAddBasecampProject = () => {
    setAddMenuOpen(false)
    handleAddDocument()
  }

  const handleSearchDocuments = () => {
    setAddMenuOpen(false)
    handleAddDocument()
  }

  const handleBrowseProjects = () => {
    setAddMenuOpen(false)
    // Coming soon
    console.log('Browse projects - coming soon')
  }

  const handleRecentDocuments = () => {
    setAddMenuOpen(false)
    handleAddDocument()
  }

  const handleAISuggest = () => {
    setAddMenuOpen(false)
    // Coming soon
    console.log('AI suggest context - coming soon')
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <div className="border-b p-4 bg-sidebar">
        <div className="flex items-center gap-3 mb-2">
          <a
            href="/?test=index"
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Prototype Index</span>
          </a>
        </div>
        <h1 className="text-xl font-bold mb-1">Chat Window Design Prototype</h1>
        <p className="text-sm text-muted-foreground mb-1">
          V0-inspired design improvements - refine HTML/CSS in isolation
        </p>
        <p className="text-xs text-muted-foreground mb-3">
          <strong>Status:</strong> Phases 1, 2, 4, 5 complete | Phase 3 (File Review) pending | Chat-First Home shipped in production ChatPane
        </p>

        {/* Tab Switcher */}
        <div className="flex gap-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted hover:bg-muted/80'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {activeTab === 'chat-first' && (
          <div className="h-full flex flex-col items-center justify-center max-w-2xl mx-auto px-4">
            {/* Minimal Welcome */}
            <div className="text-center mb-8">
              <h1 className="inline-flex items-center gap-2 text-3xl font-normal text-muted-foreground">
                <span className="text-2xl">✨</span>
                <span>Welcome back, Rowan</span>
              </h1>
            </div>

            {/* Large Input Area */}
            <div className="w-full">
              <ContentEditableChatInput
                value={input}
                references={references}
                onChange={setInput}
                onReferencesChange={setReferences}
                showToolbar={true}
                leftAccessory={
                  <DropdownMenu open={addMenuOpen} onOpenChange={setAddMenuOpen}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 rounded-md hover:bg-muted text-muted-foreground"
                      >
                        <Plus className="h-5 w-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-64">
                      <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                        Add to conversation
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="gap-3 py-2.5" onClick={handleAddDocument}>
                        <FileText className="h-4 w-4" />
                        <span>Add document(s)</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem className="gap-3 py-2.5" onClick={handleAddVoiceMemo}>
                        <Mic className="h-4 w-4" />
                        <span>Add voice memo</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem className="gap-3 py-2.5" onClick={handleAddFromInbox}>
                        <Inbox className="h-4 w-4" />
                        <span>Add from inbox</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                        Basecamp
                      </DropdownMenuLabel>
                      <DropdownMenuItem className="gap-3 py-2.5" onClick={handleAddBasecampTodo}>
                        <span className="text-base">☑️</span>
                        <span>Add todo</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem className="gap-3 py-2.5" onClick={handleAddBasecampProject}>
                        <Users className="h-4 w-4" />
                        <span>Add project</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                        Search & Browse
                      </DropdownMenuLabel>
                      <DropdownMenuItem className="gap-3 py-2.5" onClick={handleSearchDocuments}>
                        <Search className="h-4 w-4" />
                        <span>Search documents</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem className="gap-3 py-2.5" onClick={handleBrowseProjects}>
                        <FolderOpen className="h-4 w-4" />
                        <span>Browse projects</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem className="gap-3 py-2.5" onClick={handleRecentDocuments}>
                        <Clock className="h-4 w-4" />
                        <span>Recent documents</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="gap-3 py-2.5 text-primary" onClick={handleAISuggest}>
                        <Sparkles className="h-4 w-4" />
                        <span>AI suggest context</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                }
                modelBadge={
                  <Select value={selectedModel} onValueChange={setSelectedModel}>
                    <SelectTrigger className="h-7 gap-1.5 px-2 text-xs border-0 bg-transparent hover:bg-muted/50 rounded-md">
                      <SelectValue>
                        {selectedModel === 'claude-sonnet-4-5-20250929' ? 'Sonnet 4.5' : 'GPT-4o'}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="claude-sonnet-4-5-20250929">Sonnet 4.5</SelectItem>
                      <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                    </SelectContent>
                  </Select>
                }
                agentBadge={null}
                onSend={() => console.log('Send clicked')}
                className="min-h-[180px] text-base"
                placeholder="How can I help you today?"
              />
            </div>

          </div>
        )}

        {activeTab === 'streaming-output' && (
          <div className="max-w-3xl mx-auto space-y-8">
            <div>
              <h2 className="text-lg font-semibold mb-2">Streaming output</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Narrow scrollable output window for agent actions (e.g. command run, document creation). Lines stream in real time.
              </p>
            </div>

            <div className="space-y-4">
              <h3 className="text-md font-medium">Command block</h3>
              <div className="space-y-2">
                <StreamingOutputWindow
                  title="Ran command: mkdir -p /Users/rowan/hopwork/clients/msaliceb/.cursor/skills/doc-sync-commit"
                  lines={commandOutputLines}
                  isStreaming={commandStreaming}
                  linesAdded={commandOutputLines.length > 0 ? commandOutputLines.length : undefined}
                  maxHeight={125}
                />
                <Button variant="outline" size="sm" onClick={runCommandDemo} disabled={commandStreaming}>
                  {commandStreaming ? 'Streaming…' : 'Run command demo'}
                </Button>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-md font-medium">Doc creation block</h3>
              <div className="space-y-2">
                <StreamingOutputWindow
                  title="docs/implementations/2026-03-09-streaming-output-window.md"
                  lines={docOutputLines}
                  isStreaming={docStreaming}
                  linesAdded={docOutputLines.length > 0 ? docOutputLines.length : undefined}
                  maxHeight={125}
                  documentPath="docs/implementations/2026-03-09-streaming-output-window.md"
                />
                <Button variant="outline" size="sm" onClick={runDocDemo} disabled={docStreaming}>
                  {docStreaming ? 'Streaming…' : 'Run doc creation demo'}
                </Button>
              </div>
            </div>

            <div className="p-4 bg-muted/30 rounded-lg border border-border">
              <h4 className="text-sm font-semibold mb-2">Usage</h4>
              <p className="text-xs text-muted-foreground">
                This component can be used in chat when the agent is writing a document or running a command, to contain the raw output lines. Optional: integrate into Full Chat so a simulated doc/command response shows this window below the assistant message.
              </p>
            </div>
          </div>
        )}

        {activeTab === 'toolbar' && (
          <div className="max-w-3xl mx-auto space-y-6">
            <h2 className="text-lg font-semibold">Bottom Toolbar</h2>
            <div className="border rounded-lg p-4 bg-card">
              <ContentEditableChatInput
                value={input}
                references={references}
                onChange={setInput}
                onReferencesChange={setReferences}
                showToolbar={true}
                modelBadge={
                  <Select value={selectedModel} onValueChange={setSelectedModel}>
                    <SelectTrigger className="h-8 gap-2 px-3 text-xs border-border bg-background hover:bg-muted max-w-[200px]">
                      <Brain className="w-3 h-3 shrink-0" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="claude-sonnet-4-5-20250929" className="whitespace-nowrap">Claude Sonnet 4.5</SelectItem>
                      <SelectItem value="gpt-4o" className="whitespace-nowrap">GPT-4o</SelectItem>
                    </SelectContent>
                  </Select>
                }
                agentBadge={null}
                onSend={() => console.log('Send clicked')}
              />
            </div>
          </div>
        )}

        {activeTab === 'messages' && (
          <div className="max-w-3xl mx-auto space-y-6">
            <h2 className="text-lg font-semibold">Message Design</h2>
            <div className="space-y-4">
              {MOCK_MESSAGES.map((msg, i) => {
                const isEditing = i === 0
                const isAfterEdited = isEditing && i > 0

                return (
                <div
                  key={i}
                  className={cn(
                    "w-full group transition-opacity",
                    isAfterEdited && "opacity-40"
                  )}
                >
                  {msg.role === 'assistant' ? (
                    <div className="flex justify-start">
                      <div className="max-w-[85%] space-y-2">
                        <div className="prose prose-sm dark:prose-invert max-w-none text-[15px] leading-relaxed prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-pre:my-2 prose-blockquote:my-2 prose-table:my-2">
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            rehypePlugins={[rehypeHighlight]}
                            components={{
                              p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                              ul: ({ children }) => <ul className="list-disc ml-4 mb-2">{children}</ul>,
                              ol: ({ children }) => <ol className="list-decimal ml-4 mb-2">{children}</ol>,
                              li: ({ children }) => <li className="mb-0.5">{children}</li>,
                              strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                              code: ({ className, children, ...props }) => {
                                const isBlock = className?.startsWith('language-')
                                if (isBlock) {
                                  return (
                                    <pre className="bg-muted rounded-lg p-3 overflow-x-auto text-sm my-2">
                                      <code className={className} {...props}>{children}</code>
                                    </pre>
                                  )
                                }
                                return <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono" {...props}>{children}</code>
                              },
                              blockquote: ({ children }) => (
                                <blockquote className="border-l-4 border-border pl-3 my-2 text-muted-foreground italic">
                                  {children}
                                </blockquote>
                              ),
                              table: ({ children }) => (
                                <div className="overflow-x-auto my-2">
                                  <table className="min-w-full border border-border rounded-md text-sm">
                                    {children}
                                  </table>
                                </div>
                              ),
                              th: ({ children }) => (
                                <th className="border border-border bg-muted/50 px-3 py-2 text-left font-medium">
                                  {children}
                                </th>
                              ),
                              td: ({ children }) => (
                                <td className="border border-border px-3 py-2">{children}</td>
                              ),
                            }}
                          >
                            {msg.content}
                          </ReactMarkdown>
                        </div>
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground hover:text-foreground">
                            Copy
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-start">
                      <div className="w-full rounded-2xl bg-primary/10 border border-primary/20 px-4 py-3 space-y-2">
                        <p className="text-[15px] leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground hover:text-foreground">
                            Edit
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )
              })}
            </div>

            <div className="mt-6 p-4 bg-muted/30 rounded-lg border border-border">
              <h4 className="text-sm font-semibold mb-2">Cursor-Style Edit Feature:</h4>
              <p className="text-xs text-muted-foreground">
                When editing a message, subsequent messages become semi-transparent (40% opacity) to show they'll be regenerated.
                This matches Cursor's UX pattern.
              </p>
            </div>
          </div>
        )}

        {activeTab === 'mention' && (
          <div className="max-w-3xl mx-auto space-y-6">
            <h2 className="text-lg font-semibold">Mention Dropdown</h2>
            <p className="text-sm text-muted-foreground">
              Test the @ mention autocomplete component with live search
            </p>

            <div className="border rounded-lg p-4 bg-card space-y-4">
              {/* Controls */}
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      setMentionQuery('')
                      const rect = mentionSearchInputRef.current?.getBoundingClientRect()
                      setMentionPosition(rect ? { top: rect.bottom + 6, left: rect.left } : { top: 300, left: 100 })
                    }}
                    variant="outline"
                  >
                    Show Suggestions (Empty Query)
                  </Button>
                  <Button
                    onClick={() => {
                      setMentionQuery('ggia')
                      const rect = mentionSearchInputRef.current?.getBoundingClientRect()
                      setMentionPosition(rect ? { top: rect.bottom + 6, left: rect.left } : { top: 300, left: 100 })
                    }}
                    variant="outline"
                  >
                    Search "ggia"
                  </Button>
                  <Button
                    onClick={() => {
                      setMentionQuery('')
                      setMentionPosition(null)
                    }}
                    variant="ghost"
                  >
                    Close
                  </Button>
                </div>

                {/* Custom search input - dropdown anchors below this */}
                <div className="flex gap-2">
                  <input
                    ref={mentionSearchInputRef}
                    type="text"
                    placeholder="Type a search query..."
                    className="flex-1 px-3 py-2 text-sm border border-border rounded-md bg-background"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const target = e.target as HTMLInputElement
                        setMentionQuery(target.value)
                        const rect = mentionSearchInputRef.current?.getBoundingClientRect()
                        setMentionPosition(rect ? { top: rect.bottom + 6, left: rect.left } : { top: 300, left: 100 })
                      }
                    }}
                  />
                  <span className="text-xs text-muted-foreground self-center">
                    Press Enter to search
                  </span>
                </div>
              </div>

              {/* Mention dropdown */}
              {mentionPosition && (
                <MentionAutocomplete
                  query={mentionQuery}
                  position={mentionPosition}
                  onSelect={(ref) => {
                    console.log('Selected:', ref)
                    alert(`Selected: ${ref.title}\nID: ${ref.id}\nType: ${ref.type}`)
                    setMentionQuery('')
                    setMentionPosition(null)
                  }}
                  onClose={() => {
                    setMentionQuery('')
                    setMentionPosition(null)
                  }}
                />
              )}

              {/* Status indicator */}
              <div className="text-xs text-muted-foreground">
                {mentionPosition ? (
                  <span className="text-green-600">
                    ✓ Dropdown visible {mentionQuery ? `(searching: "${mentionQuery}")` : '(showing suggestions)'}
                  </span>
                ) : (
                  <span className="text-muted-foreground/70">Dropdown closed</span>
                )}
              </div>
            </div>

            <div className="p-4 bg-muted/30 rounded-lg border border-border">
              <h4 className="text-sm font-semibold mb-2">Features to Test:</h4>
              <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                <li>Empty query shows AI-powered suggestions</li>
                <li>Typing searches documents, folders, and Basecamp items</li>
                <li>Arrow keys (↑↓) navigate results</li>
                <li>Enter selects highlighted item</li>
                <li>Escape closes dropdown</li>
                <li>Shows document icons and colors</li>
                <li>Folders show document count</li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'drag-drop' && (
          <div className="max-w-4xl mx-auto space-y-6">
            <div>
              <h2 className="text-lg font-semibold mb-2">Drag & Drop Test</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Drag items from the sidebar below into the chat input to test drag & drop functionality.
                Basecamp todos/projects are draggable but not pasteable.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-6">
              {/* Mock Sidebar/Tabs */}
              <div className="space-y-4">
                <h3 className="text-md font-medium">Mock Sidebar / Tabs</h3>
                <div className="space-y-2">
                  {mockDraggableItems.map((item) => (
                    <div
                      key={item.id}
                      draggable
                      onDragStart={(e) => {
                        // Set drag data in the format expected by ContentEditableChatInput
                        const dragData = JSON.stringify({
                          type: item.id.startsWith('basecamp-') ? 'tab' : 'document',
                          id: item.id,
                          title: item.title,
                          name: item.title,
                          documentType: item.type,
                          path: item.path
                        })
                        e.dataTransfer.setData('application/json', dragData)
                        e.dataTransfer.effectAllowed = 'copy'
                        console.log('[Drag Test] Started dragging:', item.title)
                      }}
                      className="p-3 bg-card border border-border rounded-md cursor-move hover:bg-accent transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-primary" />
                        <span className="text-sm font-medium">{item.title}</span>
                      </div>
                      {item.path && (
                        <div className="text-xs text-muted-foreground mt-1 ml-4">
                          {item.path}
                        </div>
                      )}
                      {item.id.startsWith('basecamp-') && (
                        <div className="text-xs text-muted-foreground mt-1 ml-4">
                          Basecamp {item.id.includes('todo') ? 'Todo' : 'Project'}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Chat Input */}
              <div className="space-y-4">
                <h3 className="text-md font-medium">Chat Input (Drop Target)</h3>
                <ContentEditableChatInput
                  value={input}
                  references={references}
                  onChange={setInput}
                  onReferencesChange={setReferences}
                  onInsertReference={(ref) => {
                    console.log('[Drag Test] Reference inserted:', ref)
                    setReferences([...references, ref])
                  }}
                  showToolbar={true}
                  modelBadge={
                    <Select value={selectedModel} onValueChange={setSelectedModel}>
                      <SelectTrigger className="h-8 gap-2 px-3 text-xs border-border bg-background hover:bg-muted max-w-[200px]">
                        <Brain className="w-3 h-3 shrink-0" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="claude-sonnet-4-5-20250929" className="whitespace-nowrap">Claude Sonnet 4.5</SelectItem>
                        <SelectItem value="gpt-4o" className="whitespace-nowrap">GPT-4o</SelectItem>
                      </SelectContent>
                    </Select>
                  }
                  agentBadge={null}
                  onSend={() => {
                    console.log('Send:', input)
                    setInput('')
                  }}
                />

                {/* Show current references - linked to inline chips */}
                {references.length > 0 && (
                  <div className="mt-4 p-3 bg-muted/30 rounded-md border border-border/50">
                    <div className="text-xs text-muted-foreground mb-2 font-medium">Current References ({references.length}):</div>
                    <div className="flex flex-wrap gap-1.5">
                      {references.map((ref, index) => {
                        // Build display text with line numbers if present
                        const displayText = ref.lineStart !== undefined
                          ? `${ref.title} (${ref.lineStart}${ref.lineEnd && ref.lineEnd !== ref.lineStart ? `-${ref.lineEnd}` : ''})`
                          : ref.title

                        // Build tooltip
                        const tooltipParts: string[] = []
                        if (ref.path) {
                          tooltipParts.push(ref.path)
                        } else if (ref.title) {
                          tooltipParts.push(ref.title)
                        }
                        if (ref.lineStart !== undefined) {
                          if (ref.lineEnd !== undefined && ref.lineEnd !== ref.lineStart) {
                            tooltipParts.push(`(${ref.lineStart}-${ref.lineEnd})`)
                          } else {
                            tooltipParts.push(`(${ref.lineStart})`)
                          }
                        }
                        const tooltip = tooltipParts.join(' ')

                        // Create unique key that includes line range for multiple references to same doc
                        const uniqueKey = `${ref.id}-${ref.lineStart ?? 'none'}-${ref.lineEnd ?? 'none'}-${index}`

                        // Helper to check if two references match exactly (for removal)
                        const refMatches = (r1: typeof ref, r2: typeof ref) => {
                          if (r1.id !== r2.id) return false
                          return r1.lineStart === r2.lineStart && r1.lineEnd === r2.lineEnd
                        }

                        return (
                          <Badge
                            key={uniqueKey}
                            variant="outline"
                            className="flex items-center gap-1 text-xs pl-1.5 pr-1 py-0.5 cursor-pointer hover:bg-muted/50 transition-colors"
                            title={tooltip}
                            onClick={() => {
                              // Remove from references array (match by ID + line range)
                              setReferences(refs => refs.filter(r => !refMatches(r, ref)))
                              // Also remove inline chip from contenteditable
                              const editor = document.querySelector('[contenteditable="true"]') as HTMLElement
                              if (editor) {
                                // Find chip by ID and line range (check title attribute which has the tooltip)
                                const chips = editor.querySelectorAll(`[data-reference-id="${ref.id}"]`)
                                chips.forEach(chip => {
                                  const chipTitle = chip.getAttribute('title') || ''
                                  const expectedTooltip = tooltip
                                  if (chipTitle === expectedTooltip || chipTitle.includes(`(${ref.lineStart}`)) {
                                    chip.remove()
                                  }
                                })
                                // Trigger input to sync state
                                const inputEvent = new Event('input', { bubbles: true })
                                editor.dispatchEvent(inputEvent)
                              }
                            }}
                          >
                            <span className="max-w-[120px] truncate">{displayText}</span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                // Remove from references array (match by ID + line range)
                                setReferences(refs => refs.filter(r => !refMatches(r, ref)))
                                // Also remove inline chip from contenteditable
                                const editor = document.querySelector('[contenteditable="true"]') as HTMLElement
                                if (editor) {
                                  // Find chip by ID and line range
                                  const chips = editor.querySelectorAll(`[data-reference-id="${ref.id}"]`)
                                  chips.forEach(chip => {
                                    const chipTitle = chip.getAttribute('title') || ''
                                    const expectedTooltip = tooltip
                                    if (chipTitle === expectedTooltip || chipTitle.includes(`(${ref.lineStart}`)) {
                                      chip.remove()
                                    }
                                  })
                                  // Trigger input to sync state
                                  const inputEvent = new Event('input', { bubbles: true })
                                  editor.dispatchEvent(inputEvent)
                                }
                              }}
                              className="ml-0.5 hover:bg-muted rounded-sm p-0.5"
                            >
                              ×
                            </button>
                          </Badge>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 p-4 bg-muted/30 rounded-lg border border-border">
              <h4 className="text-sm font-semibold mb-2">Testing Instructions:</h4>
              <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                <li>Drag items from the left sidebar into the chat input on the right</li>
                <li>Items should appear as reference chips in the chat input</li>
                <li>Basecamp todos/projects are draggable (test with the Basecamp items)</li>
                <li>Markdown documents can be pasted with file references (e.g., @App.tsx (22-23))</li>
                <li>Check the browser console for drag/drop event logs</li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'copy-paste' && (
          <div className="max-w-6xl mx-auto space-y-6">
            <div>
              <h2 className="text-lg font-semibold mb-2">Copy/Paste Test</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Select text in the markdown editor (left) and copy it. Then paste into the chat input (right).
                Full line selections will create reference chips with line numbers.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-6">
              {/* Markdown Editor with View Mode Toggle */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-md font-medium">Markdown Editor</h3>
                  <div className="flex gap-2">
                    <Button
                      variant={editorViewMode === 'source' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setEditorViewMode('source')}
                      className="text-xs h-7"
                    >
                      Source
                    </Button>
                    <Button
                      variant={editorViewMode === 'wysiwyg' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setEditorViewMode('wysiwyg')}
                      className="text-xs h-7"
                    >
                      Visual
                    </Button>
                  </div>
                </div>
                <div className="border rounded-lg overflow-hidden bg-card" style={{ height: '500px' }}>
                  <MarkdownEditor
                    initialContent={`# Test Document

This is paragraph one. It contains some simple text for testing.

This is paragraph two. It has different content to help with matching.

This is paragraph three. Each paragraph is on its own line in the source.

This is paragraph four. The last paragraph for our minimal test case.`}
                    onChange={() => {}}
                    darkMode={true}
                    viewMode={editorViewMode}
                    readOnly={false}
                    documentId="test-doc.md"
                    onCopyWithReference={(data: { text: string; lineStart?: number; lineEnd?: number; isFullLine: boolean }) => {
                      console.log('[Copy/Paste Test] Copy event:', data)
                      if (data.isFullLine && data.lineStart) {
                        // The clipboard data is already set by the editor
                        // The paste handler will pick it up
                      }
                    }}
                  />
                </div>
                <div className="text-xs text-muted-foreground">
                  {editorViewMode === 'source' ? (
                    <>
                      <strong>Source Mode:</strong> Select full line(s) (including line numbers) to create chips. Select partial text for plain text copy.
                    </>
                  ) : (
                    <>
                      <strong>Visual Mode:</strong> Select text in the visual editor. Full line matches will create chips with line numbers. Partial selections copy as text.
                    </>
                  )}
                </div>
              </div>

              {/* Chat Input */}
              <div className="space-y-4">
                <h3 className="text-md font-medium">Chat Input (Paste Target)</h3>
                <ContentEditableChatInput
                  value={input}
                  references={references}
                  onChange={setInput}
                  onReferencesChange={setReferences}
                  onInsertReference={(ref) => {
                    console.log('[Copy/Paste Test] Reference inserted:', ref)
                    setReferences([...references, ref])
                  }}
                  showToolbar={true}
                  modelBadge={
                    <Select value={selectedModel} onValueChange={setSelectedModel}>
                      <SelectTrigger className="h-8 gap-2 px-3 text-xs border-border bg-background hover:bg-muted max-w-[200px]">
                        <Brain className="w-3 h-3 shrink-0" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="claude-sonnet-4-5-20250929" className="whitespace-nowrap">Claude Sonnet 4.5</SelectItem>
                        <SelectItem value="gpt-4o" className="whitespace-nowrap">GPT-4o</SelectItem>
                      </SelectContent>
                    </Select>
                  }
                  agentBadge={null}
                  onSend={() => {
                    console.log('Send:', input)
                    setInput('')
                  }}
                />

                {/* Show current references */}
                {references.length > 0 && (
                  <div className="mt-4 p-3 bg-muted/30 rounded-md border border-border/50">
                    <div className="text-xs text-muted-foreground mb-2 font-medium">Current References ({references.length}):</div>
                    <div className="flex flex-wrap gap-1.5">
                      {references.map((ref) => (
                        <Badge key={ref.id} variant="outline" className="flex items-center gap-1 text-xs pl-1.5 pr-1 py-0.5">
                          <span className="max-w-[120px] truncate">{ref.title}</span>
                          {ref.lineStart && (
                            <span className="text-muted-foreground">
                              ({ref.lineStart}{ref.lineEnd && ref.lineEnd !== ref.lineStart ? `-${ref.lineEnd}` : ''})
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => setReferences(refs => refs.filter(r => r.id !== ref.id))}
                            className="ml-0.5 hover:bg-muted rounded-sm p-0.5"
                          >
                            ×
                          </button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'review' && (
          <div className="max-w-3xl mx-auto space-y-6">
            <div>
              <h2 className="text-lg font-semibold mb-2">File Review Mechanism (Current)</h2>
              <p className="text-sm text-muted-foreground mb-4">
                <strong>Status:</strong> FileEditWidget exists and works, but Phase 3 (inline expandable section) not implemented yet.
                <br />
                <strong>Current:</strong> Widget appears in messages, opens DiffPreviewDialog modal for review.
                <br />
                <strong>Phase 3 Goal:</strong> Replace modal with inline expandable FileReviewSection component.
              </p>
            </div>

            {/* Current FileEditWidget (as it appears in messages) */}
            <div className="space-y-4">
              <h3 className="text-md font-medium">Pending Review</h3>
              <FileEditWidget
                fileName="frontend/src/components/layout/chat-pane.tsx"
                additions={26}
                deletions={13}
                status="pending"
                requiresAcceptance={true}
                onReview={() => alert('Would open DiffPreviewDialog modal')}
                onAccept={() => alert('Would accept changes')}
                onReject={() => alert('Would reject changes')}
              />

              <h3 className="text-md font-medium mt-6">Accepted</h3>
              <FileEditWidget
                fileName="frontend/src/components/ui/mention-autocomplete.tsx"
                additions={45}
                deletions={12}
                status="accepted"
                changeStatus="accepted"
              />

              <h3 className="text-md font-medium mt-6">Rejected</h3>
              <FileEditWidget
                fileName="frontend/src/components/layout/chat-pane.tsx"
                additions={8}
                deletions={5}
                status="rejected"
                changeStatus="rejected"
              />
            </div>

            <div className="mt-6 p-4 bg-muted/30 rounded-lg border border-border">
              <h4 className="text-sm font-semibold mb-2">Phase 3 Implementation Notes:</h4>
              <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                <li>Create <code>FileReviewSection</code> component (expandable, shows file list)</li>
                <li>Position above input (inline, not modal)</li>
                <li>Integrate with existing diff infrastructure</li>
                <li>Replace DiffPreviewDialog usage</li>
                <li>Add keyboard shortcuts (⌘↵ for Keep All)</li>
              </ul>
            </div>
          </div>
        )}

        {(activeTab === 'full' || activeTab === 'modified-files-above-chat') && (() => {
          const showModifiedFilesBar = activeTab === 'modified-files-above-chat'
          function FullChatBody() {
            return (
              <>
            {/* Chat-First Home View (when no conversation started) */}
            {conversationHistory.length === 0 && !isResponding && (
              <div className="flex-1 flex flex-col items-center justify-center max-w-5xl mx-auto px-6">
                {/* Minimal Welcome */}
                <div className="text-center mb-8">
                  <h1 className="inline-flex items-center gap-2 text-3xl font-normal text-muted-foreground">
                    <span className="text-2xl">✨</span>
                    <span>Welcome back, Rowan</span>
                  </h1>
                </div>

                {/* Large Input Area */}
                <div className="w-full">
                  <ContentEditableChatInput
                    value={input}
                    references={references}
                    onChange={setInput}
                    onReferencesChange={setReferences}
                    onKeyDown={(e) => {
                      // Submit on Enter (without Shift)
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        if (input.trim()) {
                          // Add user message to conversation history
                          setConversationHistory(prev => [...prev, {
                            role: 'user',
                            content: input,
                            timestamp: new Date()
                          }])

                          setInput('')
                          setReferences([])

                          // Prepare for streaming
                          setIsResponding(true)
                          setStreamingText('')
                          setStreamingIndex(0)
                          setWasStopped(false)
                          setFullResponse('')

                          // Simulate "thinking" delay, then set full response to stream
                          setTimeout(() => {
                            const mockResponse = `This is a demo response with token-based streaming.

Notice how:
• Words appear as complete units (not character-by-character)
• The streaming feels faster and more natural
• You can scroll up to see your previous messages
• The conversation history builds up over time
• Each exchange is preserved

You can stop generation at any time with the STOP button, and you can click any previous prompt to edit and rewind the conversation!`

                            setFullResponse(mockResponse)
                            setStreamingIndex(0)
                          }, 800)
                        }
                      }
                    }}
                    showToolbar={true}
                    leftAccessory={
                      <DropdownMenu open={addMenuOpen} onOpenChange={setAddMenuOpen}>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 rounded-md hover:bg-muted text-muted-foreground"
                          >
                            <Plus className="h-5 w-5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-64">
                          <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                            Add to conversation
                          </DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="gap-3 py-2.5" onClick={handleAddDocument}>
                            <FileText className="h-4 w-4" />
                            <span>Add document(s)</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem className="gap-3 py-2.5" onClick={handleAddVoiceMemo}>
                            <Mic className="h-4 w-4" />
                            <span>Add voice memo</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem className="gap-3 py-2.5" onClick={handleAddFromInbox}>
                            <Inbox className="h-4 w-4" />
                            <span>Add from inbox</span>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                            Basecamp
                          </DropdownMenuLabel>
                          <DropdownMenuItem className="gap-3 py-2.5" onClick={handleAddBasecampTodo}>
                            <span className="text-base">☑️</span>
                            <span>Add todo</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem className="gap-3 py-2.5" onClick={handleAddBasecampProject}>
                            <Users className="h-4 w-4" />
                            <span>Add project</span>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                            Search & Browse
                          </DropdownMenuLabel>
                          <DropdownMenuItem className="gap-3 py-2.5" onClick={handleSearchDocuments}>
                            <Search className="h-4 w-4" />
                            <span>Search documents</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem className="gap-3 py-2.5" onClick={handleBrowseProjects}>
                            <FolderOpen className="h-4 w-4" />
                            <span>Browse projects</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem className="gap-3 py-2.5" onClick={handleRecentDocuments}>
                            <Clock className="h-4 w-4" />
                            <span>Recent documents</span>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="gap-3 py-2.5 text-primary" onClick={handleAISuggest}>
                            <Sparkles className="h-4 w-4" />
                            <span>AI suggest context</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    }
                    modelBadge={
                      <Select value={selectedModel} onValueChange={setSelectedModel}>
                        <SelectTrigger className="h-7 gap-1.5 px-2 text-xs border-0 bg-transparent hover:bg-muted/50 rounded-md">
                          <SelectValue>
                            {selectedModel === 'claude-sonnet-4-5-20250929' ? 'Sonnet 4.5' : 'GPT-4o'}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="claude-sonnet-4-5-20250929">Sonnet 4.5</SelectItem>
                          <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                        </SelectContent>
                      </Select>
                    }
                    agentBadge={null}
                    onSend={() => {
                      if (!input.trim()) return

                      // Add user message to conversation history
                      setConversationHistory(prev => [...prev, {
                        role: 'user',
                        content: input,
                        timestamp: new Date()
                      }])

                      setInput('')
                      setReferences([])

                      // Prepare for streaming
                      setIsResponding(true)
                      setStreamingText('')
                      setStreamingIndex(0)
                      setWasStopped(false)
                      setFullResponse('')

                      // Simulate "thinking" delay, then set full response to stream
                      setTimeout(() => {
                        const mockResponse = `This is a demo response with token-based streaming.

Notice how:
• Words appear as complete units
• The streaming feels faster and more natural
• You can scroll up to see your previous messages
• The conversation history builds up over time
• Each exchange is preserved

You can stop generation at any time with the STOP button!`

                        setFullResponse(mockResponse)
                        setStreamingIndex(0)
                      }, 800)
                    }}
                    placeholder="How can I help you today? (Press Enter to send, Shift+Enter for new line)"
                    className="min-h-[180px] text-base"
                  />
                </div>
              </div>
            )}

            {/* Full Chat View (after conversation started) */}
            {(conversationHistory.length > 0 || isResponding) && (
              <div className="h-full flex flex-col max-w-5xl mx-auto w-full px-6 relative">
                {/* Layout guide toggle (Phase 4) */}
                <button
                  onClick={() => setShowLayoutGuides(!showLayoutGuides)}
                  className="absolute top-4 right-4 z-20 px-2 py-1 text-xs bg-muted rounded border border-border hover:bg-muted/80"
                >
                  {showLayoutGuides ? 'Hide' : 'Show'} guides
                </button>

                {/* Layout annotations (Phase 4) */}
                {showLayoutGuides && (
                  <>
                    <div className="absolute top-0 right-0 px-2 py-0.5 text-xs bg-green-500/20 text-green-300 border-l border-b border-green-500/50 rounded-bl pointer-events-none">
                      Scrollable Area (scroll up to see prompt)
                    </div>
                    <div className="absolute bottom-0 right-0 px-2 py-0.5 text-xs bg-purple-500/20 text-purple-300 border-l border-t border-purple-500/50 rounded-tl pointer-events-none">
                      Fixed Input (always visible at bottom)
                    </div>
                  </>
                )}

                {/* Scrollable Content Area (shows full conversation history) */}
                <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-4 py-6 space-y-4">
                  {/* Render all messages from conversation history */}
                  {conversationHistory.map((message, index) => (
                    <div key={`${message.timestamp.getTime()}-${index}`} className={cn(
                      message.role === 'user'
                        ? 'w-full rounded-2xl bg-primary/10 border border-primary/20'
                        : 'max-w-[85%]'
                    )}>
                      {message.role === 'user' ? (
                        // User message (editable)
                        editingMessageIndex === index ? (
                          // Edit mode: same prompt interface as main chat (Cursor-style)
                          <div className="rounded-2xl bg-primary/10 border border-primary/20 overflow-hidden">
                            <ContentEditableChatInput
                              value={editedPromptText}
                              references={references}
                              onChange={setEditedPromptText}
                              onReferencesChange={setReferences}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                  setEditingMessageIndex(null)
                                  setEditedPromptText('')
                                }
                                if (e.key === 'Enter' && !e.shiftKey && editedPromptText.trim()) {
                                  e.preventDefault()
                                  handleResubmit()
                                }
                              }}
                              showToolbar={true}
                              leftAccessory={
                                <DropdownMenu open={addMenuOpen} onOpenChange={setAddMenuOpen}>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 rounded-md hover:bg-muted text-muted-foreground"
                                    >
                                      <Plus className="h-5 w-5" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="start" className="w-64">
                                    <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                                      Add to conversation
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem className="gap-3 py-2.5" onClick={handleAddDocument}>
                                      <FileText className="h-4 w-4" />
                                      <span>Add document(s)</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem className="gap-3 py-2.5" onClick={handleAddVoiceMemo}>
                                      <Mic className="h-4 w-4" />
                                      <span>Add voice memo</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem className="gap-3 py-2.5" onClick={handleAddFromInbox}>
                                      <Inbox className="h-4 w-4" />
                                      <span>Add from inbox</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                                      Basecamp
                                    </DropdownMenuLabel>
                                    <DropdownMenuItem className="gap-3 py-2.5" onClick={handleAddBasecampTodo}>
                                      <span className="text-base">☑️</span>
                                      <span>Add todo</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem className="gap-3 py-2.5" onClick={handleAddBasecampProject}>
                                      <Users className="h-4 w-4" />
                                      <span>Add project</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                                      Search & Browse
                                    </DropdownMenuLabel>
                                    <DropdownMenuItem className="gap-3 py-2.5" onClick={handleSearchDocuments}>
                                      <Search className="h-4 w-4" />
                                      <span>Search documents</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem className="gap-3 py-2.5" onClick={handleBrowseProjects}>
                                      <FolderOpen className="h-4 w-4" />
                                      <span>Browse projects</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem className="gap-3 py-2.5" onClick={handleRecentDocuments}>
                                      <Clock className="h-4 w-4" />
                                      <span>Recent documents</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem className="gap-3 py-2.5 text-primary" onClick={handleAISuggest}>
                                      <Sparkles className="h-4 w-4" />
                                      <span>AI suggest context</span>
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              }
                              modelBadge={
                                <Select value={selectedModel} onValueChange={setSelectedModel}>
                                  <SelectTrigger className="h-7 gap-1.5 px-2 text-xs border-0 bg-transparent hover:bg-muted/50 rounded-md">
                                    <SelectValue>
                                      {selectedModel === 'claude-sonnet-4-5-20250929' ? 'Sonnet 4.5' : 'GPT-4o'}
                                    </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="claude-sonnet-4-5-20250929">Sonnet 4.5</SelectItem>
                                      <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                                    </SelectContent>
                                  </Select>
                              }
                              agentBadge={null}
                              onSend={handleResubmit}
                              placeholder="Edit message... (Send to resubmit, Esc to cancel)"
                              className="min-h-[100px] text-base border-0 bg-transparent"
                            />
                            <div className="flex items-center justify-between px-3 pb-2 pt-0">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-xs text-muted-foreground h-7"
                                onClick={() => {
                                  setEditingMessageIndex(null)
                                  setEditedPromptText('')
                                }}
                              >
                                Cancel
                              </Button>
                              <span className="text-xs text-muted-foreground">⌘↵ to send • Esc to cancel</span>
                            </div>
                          </div>
                        ) : (
                          // Read-only mode (clickable)
                          <button
                            onClick={() => {
                              if (!isResponding) {
                                setEditingMessageIndex(index)
                                setEditedPromptText(message.content)
                              }
                            }}
                            disabled={isResponding}
                            className={cn(
                              "w-full text-left px-4 py-3 transition-colors rounded-2xl",
                              !isResponding && "hover:border-primary/40 cursor-pointer",
                              isResponding && "cursor-not-allowed opacity-75"
                            )}
                          >
                            <p className="text-sm text-foreground whitespace-pre-wrap">{message.content}</p>
                            {!isResponding && (
                              <div className="text-xs text-muted-foreground mt-2">
                                Click to edit and rewind conversation
                              </div>
                            )}
                          </button>
                        )
                      ) : (
                        // Assistant message (no bubble, markdown-rendered)
                        <div className="space-y-2">
                          <div className="prose prose-sm dark:prose-invert max-w-none text-[15px] leading-relaxed prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-pre:my-2 prose-blockquote:my-2 prose-table:my-2">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              rehypePlugins={[rehypeHighlight]}
                              components={{
                                p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                                ul: ({ children }) => <ul className="list-disc ml-4 mb-2">{children}</ul>,
                                ol: ({ children }) => <ol className="list-decimal ml-4 mb-2">{children}</ol>,
                                li: ({ children }) => <li className="mb-0.5">{children}</li>,
                                strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                                code: ({ className, children, ...props }) => {
                                  const isBlock = className?.startsWith('language-')
                                  if (isBlock) {
                                    return (
                                      <pre className="bg-muted rounded-lg p-3 overflow-x-auto text-sm my-2">
                                        <code className={className} {...props}>{children}</code>
                                      </pre>
                                    )
                                  }
                                  return <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono" {...props}>{children}</code>
                                },
                                blockquote: ({ children }) => (
                                  <blockquote className="border-l-4 border-border pl-3 my-2 text-muted-foreground italic">
                                    {children}
                                  </blockquote>
                                ),
                                table: ({ children }) => (
                                  <div className="overflow-x-auto my-2">
                                    <table className="min-w-full border border-border rounded-md text-sm">
                                      {children}
                                    </table>
                                  </div>
                                ),
                                th: ({ children }) => (
                                  <th className="border border-border bg-muted/50 px-3 py-2 text-left font-medium">
                                    {children}
                                  </th>
                                ),
                                td: ({ children }) => (
                                  <td className="border border-border px-3 py-2">{children}</td>
                                ),
                              }}
                            >
                              {message.content}
                            </ReactMarkdown>
                          </div>
                          {message.wasStopped && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Square className="w-3 h-3" />
                              <span>Response stopped</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Currently streaming message (no bubble, same as assistant) */}
                  {isResponding && (
                    <div className="max-w-[85%] space-y-2">
                      {/* Thinking indicator (only show before streaming starts) */}
                      {streamingIndex === 0 && !streamingText && (
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <div className="flex gap-1">
                              <div className="w-2 h-2 rounded-full bg-primary animate-pulse" style={{ animationDelay: '0ms' }} />
                              <div className="w-2 h-2 rounded-full bg-primary animate-pulse" style={{ animationDelay: '150ms' }} />
                              <div className="w-2 h-2 rounded-full bg-primary animate-pulse" style={{ animationDelay: '300ms' }} />
                            </div>
                            <span>Thinking...</span>
                          </div>
                          <div className="text-xs text-muted-foreground/70">
                            Analyzing your question...
                          </div>
                        </div>
                      )}

                      {/* Streaming response content */}
                      {streamingText && (
                        <div className="prose prose-sm dark:prose-invert max-w-none">
                          <p className="text-[15px] leading-relaxed whitespace-pre-wrap">
                            {streamingText}
                            {/* Cursor indicator while streaming */}
                            <span className="inline-block w-1.5 h-5 ml-0.5 bg-primary animate-pulse" />
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Scroll anchor */}
                  <div ref={responseEndRef} />
                </div>

                {/* Modified files bar: above the prompt; inset .5rem L/R, rounded top corners, faded bg */}
                {showModifiedFilesBar && (
                  <div className="px-2">
                    <div className="max-w-4xl mx-auto w-full">
                      <CollapsibleModifiedFilesPanel
                        expanded={modifiedFilesPanelExpanded}
                        onToggle={() => setModifiedFilesPanelExpanded(e => !e)}
                        files={MOCK_MODIFIED_FILES}
                      />
                  </div>
                </div>
                )}

                {/* Fixed Input at Bottom (always visible) */}
                <div className="border-border pt-1 px-4">
              {/* Phase 2: Stop button (above input, only visible when streaming) */}
              {isResponding && (
                <div className="flex justify-center mb-3">
                  <Button
                    onClick={handleStop}
                    variant="destructive"
                    size="sm"
                    className="gap-2"
                  >
                    <Square className="w-4 h-4" />
                    Stop generating
                  </Button>
                </div>
              )}

              {/* AI Context */}
              {references.length > 0 && (
                <div className="mb-3 px-3 py-2 bg-muted/30 rounded-md border border-border/50">
                  <div className="text-xs text-muted-foreground mb-1.5 font-medium">AI Context:</div>
                  <div className="flex flex-wrap gap-1.5">
                    {references.map((ref) => (
                      <Badge key={ref.id} variant="outline" className="flex items-center gap-1 text-xs pl-1.5 pr-1 py-0.5">
                        <span className="max-w-[120px] truncate">{ref.title}</span>
                        <button
                          type="button"
                          onClick={() => setReferences(refs => refs.filter(r => r.id !== ref.id))}
                          className="ml-0.5 hover:bg-muted rounded-sm p-0.5"
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <ContentEditableChatInput
                value={input}
                references={references}
                onChange={setInput}
                onReferencesChange={setReferences}
                onKeyDown={(e) => {
                  // Submit on Enter (without Shift)
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    if (input.trim()) {
                      // Add user message to conversation history
                      setConversationHistory(prev => [...prev, {
                        role: 'user',
                        content: input,
                        timestamp: new Date()
                      }])

                      setInput('')
                      setReferences([])

                      // Prepare for streaming
                      setIsResponding(true)
                      setStreamingText('')
                      setStreamingIndex(0)
                      setWasStopped(false)
                      setFullResponse('')

                      // Simulate streaming response
                      setTimeout(() => {
                        const mockResponse = `Here's another response to demonstrate the conversation flow.

Notice:
• You can scroll up to see all previous messages
• Each exchange is preserved in the conversation history
• Your messages are clickable for editing and rewinding
• The conversation builds naturally over time
• Stop generation any time with the STOP button

Keep chatting - all your messages stay in the scrollable history!`

                        setFullResponse(mockResponse)
                        setStreamingIndex(0)
                      }, 800)
                    }
                  }
                }}
                showToolbar={true}
                leftAccessory={
                  <DropdownMenu open={addMenuOpen} onOpenChange={setAddMenuOpen}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 rounded-md hover:bg-muted text-muted-foreground"
                      >
                        <Plus className="h-5 w-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-64">
                      <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                        Add to conversation
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="gap-3 py-2.5" onClick={handleAddDocument}>
                        <FileText className="h-4 w-4" />
                        <span>Add document(s)</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem className="gap-3 py-2.5" onClick={handleAddVoiceMemo}>
                        <Mic className="h-4 w-4" />
                        <span>Add voice memo</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem className="gap-3 py-2.5" onClick={handleAddFromInbox}>
                        <Inbox className="h-4 w-4" />
                        <span>Add from inbox</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                        Basecamp
                      </DropdownMenuLabel>
                      <DropdownMenuItem className="gap-3 py-2.5" onClick={handleAddBasecampTodo}>
                        <span className="text-base">☑️</span>
                        <span>Add todo</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem className="gap-3 py-2.5" onClick={handleAddBasecampProject}>
                        <Users className="h-4 w-4" />
                        <span>Add project</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                        Search & Browse
                      </DropdownMenuLabel>
                      <DropdownMenuItem className="gap-3 py-2.5" onClick={handleSearchDocuments}>
                        <Search className="h-4 w-4" />
                        <span>Search documents</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem className="gap-3 py-2.5" onClick={handleBrowseProjects}>
                        <FolderOpen className="h-4 w-4" />
                        <span>Browse projects</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem className="gap-3 py-2.5" onClick={handleRecentDocuments}>
                        <Clock className="h-4 w-4" />
                        <span>Recent documents</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="gap-3 py-2.5 text-primary" onClick={handleAISuggest}>
                        <Sparkles className="h-4 w-4" />
                        <span>AI suggest context</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                }
                modelBadge={
                  <Select value={selectedModel} onValueChange={setSelectedModel}>
                    <SelectTrigger className="h-7 gap-1.5 px-2 text-xs border-0 bg-transparent hover:bg-muted/50 rounded-md">
                      <SelectValue>
                        {selectedModel === 'claude-sonnet-4-5-20250929' ? 'Sonnet 4.5' : 'GPT-4o'}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="claude-sonnet-4-5-20250929">Sonnet 4.5</SelectItem>
                      <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                    </SelectContent>
                  </Select>
                }
                agentBadge={null}
                  onSend={() => {
                    if (!input.trim()) return

                    // Add user message to conversation history
                    setConversationHistory(prev => [...prev, {
                      role: 'user',
                      content: input,
                      timestamp: new Date()
                    }])

                    setInput('')
                    setReferences([])

                    // Prepare for streaming
                    setIsResponding(true)
                    setStreamingText('')
                    setStreamingIndex(0)
                    setWasStopped(false)
                    setFullResponse('')

                    // Simulate streaming response
                    setTimeout(() => {
                      const mockResponse = `Here's another response to demonstrate the conversation flow.

Notice:
• Previous response scrolls away
• Your new prompt is now at the top
• Input is ready for the next question
• You can stop generation at any time
• You can click the prompt above to edit and rewind

You can keep chatting - each prompt stays visible at the top while the AI responds with realistic character-by-character streaming!`

                      setFullResponse(mockResponse)
                      setStreamingIndex(0)
                    }, 800)
                  }}
                  placeholder="Ask a follow-up question... (Press Enter to send, Shift+Enter for new line)"
                  className="min-h-[100px] text-base"
                />
              </div>
            </div>
            )}
              </>
            );
          }
          return (
            <div className="h-full flex flex-col">
              <FullChatBody />
            </div>
          );
        })()}
      </div>
    </div>
  )
}

